import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Bot, User, Minimize2, Maximize2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUI } from '../contexts/UIContext';
import { CONTACT } from '@/config/contact';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

const AIAssistant: React.FC = () => {
  const { language } = useLanguage();
  const { setIsAIAssistantOpen } = useUI();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = language === 'en'
        ? "Hello! I'm here to help you with questions about our digital services, packages, and pricing. How can I assist you today?"
        : "Γεια σας! Είμαι εδώ για να σας βοηθήσω με ερωτήσεις για τις ψηφιακές μας υπηρεσίες, πακέτα και τιμές. Πώς μπορώ να σας βοηθήσω σήμερα;";

      setMessages([{
        role: 'assistant',
        content: welcomeMessage,
        timestamp: Date.now()
      }]);
    }
  }, [isOpen, language, messages.length]);

  // Update UI context when chat opens/closes
  useEffect(() => {
    setIsAIAssistantOpen(isOpen);
  }, [isOpen, setIsAIAssistantOpen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversationHistory: messages,
          language: language
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: language === 'en'
          ? `I'm sorry, I'm having trouble connecting right now. Please try again or contact us directly at ${CONTACT.email}`
          : `Λυπάμαι, αντιμετωπίζω πρόβλημα σύνδεσης αυτή τη στιγμή. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε μαζί μας στο ${CONTACT.email}`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString(language === 'en' ? 'en-US' : 'el-GR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 glassmorphic-button text-white p-4 rounded-full transition-all duration-300 hover:scale-110"
        aria-label={language === 'en' ? 'Open chat' : 'Άνοιγμα συνομιλίας'}
      >
        <MessageSquare className="h-6 w-6" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 bg-black/90 backdrop-blur-sm border border-white/20 rounded-xl shadow-2xl transition-all duration-300 ${
      isMinimized ? 'w-80 h-16' : 'w-96 h-[500px]'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 glassmorphic-icon rounded-full flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-white font-medium text-sm">{language === 'en' ? 'Customer Support' : 'Εξυπηρέτηση Πελατών'}</h3>
            <p className="text-gray-400 text-xs">
              {language === 'en' ? 'Online' : 'Συνδεδεμένος'}
            </p>
          </div>
        </div>
          <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            aria-label={isMinimized ? "Επαναφορά" : "Ελαχιστοποίηση"}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" aria-hidden="true" /> : <Minimize2 className="h-4 w-4" aria-hidden="true" />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Κλείσιμο"
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-80">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-2 max-w-[80%] ${
                  message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user'
                      ? 'glassmorphic-icon'
                      : 'bg-gray-600'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="h-3 w-3 text-white" />
                    ) : (
                      <Bot className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className={`rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'glassmorphic-button text-white'
                      : 'bg-gray-700 text-gray-100'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">{formatTime(message.timestamp)}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-2 max-w-[80%]">
                  <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="h-3 w-3 text-white" />
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === 'en' ? 'Ask me anything about our services...' : 'Ρωτήστε με οτιδήποτε για τις υπηρεσίες μας...'}
                className="flex-1 glassmorphic border-0 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/20 text-sm"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Αποστολή"
                className="glassmorphic-button disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-all duration-200"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {language === 'en' 
                ? 'AI-powered support • Press Enter to send'
                : 'Υποστήριξη με AI • Πατήστε Enter για αποστολή'
              }
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAssistant;
